<script setup lang="ts">
  import { ref, useSlots } from "vue";
  import { Grabber } from "../Grabber";
  import { IconClose } from "../Icons";

  const {
    modalClass,
    close,
    grabber,
    headerVariant = "white",
    isFullHeight = false,
    index = 1,
    border = true,
  } = defineProps<{
    close?: boolean;
    grabber?: boolean;
    border?: boolean;
    headerVariant?: "primary" | "white";
    modalClass?: string;
    isFullHeight?: boolean;
    index?: number;
  }>();
  defineOptions({ inheritAttrs: false });
  const emit = defineEmits(["onClose"]);

  const slots = useSlots();
  const modalRef = ref<HTMLDivElement>(null);
  const contentRef = ref<HTMLDivElement>(null);
  const info = ref<{
    heightModal: number;
    startClickY: number;
    timeStamp: number;
    contentScrollTop: number;
    isScroll: boolean;
  }>({
    heightModal: 0,
    startClickY: 0,
    timeStamp: 0,
    contentScrollTop: 0,
    isScroll: false,
  });

  const handleClose = () => emit("onClose");

  const handleMouseDown = (event: TouchEvent) => {
    const touch = event.touches[0];

    if (touch) {
      info.value = {
        startClickY: touch.clientY,
        heightModal: modalRef.value.clientHeight,
        timeStamp: event.timeStamp,
        contentScrollTop: contentRef.value.scrollTop,
        isScroll: false,
      };
    }
  };

  const handleMouseMove = (event: TouchEvent) => {
    const touch = event.changedTouches[0];

    if (!touch) {
      return;
    }

    const delta = info.value.startClickY - touch.clientY;
    const isMoveDown = info.value.startClickY - touch.clientY < 0;
    const hasContentScroll = contentRef.value.scrollHeight > contentRef.value.clientHeight;
    const contentScrollHeight = contentRef.value.scrollHeight - contentRef.value.clientHeight;
    const isScrollContent = !(
      (isMoveDown && contentRef.value.scrollTop === 0) ||
      (!isMoveDown && contentRef.value.scrollTop === contentScrollHeight)
    );

    if (contentRef.value.contains(touch.target as Node)) {
      if (info.value.contentScrollTop !== contentRef.value.scrollTop) {
        info.value.isScroll = true;
      }

      if (hasContentScroll && isScrollContent) {
        return;
      }
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    if (!info.value.isScroll) {
      modalRef.value.style.setProperty("height", `${delta + info.value.heightModal}px`);
    }
  };

  const handleMouseUp = (event: TouchEvent) => {
    const touch = event.changedTouches[0];

    if (touch) {
      const delta = info.value.startClickY - touch.clientY;
      const isRanMinDist = delta < -50;
      const isHeightChangedTwo = info.value.heightModal / modalRef.value.clientHeight >= 2;
      const isTimeFast = event.timeStamp - info.value.timeStamp < 300;

      if ((isTimeFast && isRanMinDist && !info.value.isScroll) || isHeightChangedTwo) {
        handleClose();
      } else {
        modalRef.value.style.removeProperty("height");
      }
    }
  };
</script>

<template>
  <Teleport to="body">
    <div class="modal-background" @click="handleClose" :style="{ zIndex: index }" />
    <div
      ref="modalRef"
      class="modal"
      :class="[{ 'modal--full-height': isFullHeight }, { 'with-grabber': grabber }, modalClass]"
      :style="{ zIndex: index }"
      @touchstart="handleMouseDown"
      @touchmove="handleMouseMove"
      @touchend="handleMouseUp"
      :data-testid="$attrs['data-testid']"
    >
      <Grabber v-if="grabber" :class="`modal-grabber modal-variant_${headerVariant}`" />
      <button v-if="close" class="close" @click="handleClose" data-testid="modal-close-btn">
        <IconClose style="height: 18px; width: 18px" />
      </button>
      <div v-if="slots.header" :class="`modal-header modal-variant_${headerVariant} ${border ? 'border' : ''}`">
        <slot name="header" />
      </div>
      <div ref="contentRef" :class="`modal-content modal-variant_${headerVariant}`">
        <slot />
      </div>
      <div v-if="slots.footer" :class="`modal-footer modal-variant_${headerVariant}`">
        <slot name="footer" />
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
  .modal-background {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: var(--sf-base-background-modal);
  }

  .modal {
    display: flex;
    flex-direction: column;
    height: fit-content;
    position: absolute;
    bottom: 0;
    background: var(--sf-base-background-white);
    left: 0;
    right: 0;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    min-height: 50px;
    overflow: hidden;
    z-index: 1;
    max-height: calc(100% - 76px);
    box-shadow: var(--sf-shadow-top-m);

    &.with-grabber {
      .modal-header {
        padding: 6px 16px 12px;
      }

      .close {
        top: 22px;
      }
    }

    .close {
      display: flex;
      position: absolute;
      padding: 6px;
      right: 16px;
      top: 16px;
      background-color: var(--sf-base-background-secondary);
      border-radius: 100%;

      svg {
        stroke: var(--sf-base-icon-secondary);
      }
    }
  }

  .modal--full-height {
    height: 100%;
  }

  .modal-header {
    display: flex;
    flex-direction: column;
    padding: 16px 16px 12px;

    &.border {
      border-bottom: 1px solid var(--sf-base-border-primary);
    }

    &.modal-variant_primary {
      background-color: var(--sf-base-background-primary);
      border: none;

      &.border {
        border-bottom: none;
      }
    }

    &.modal-variant_white {
      background-color: var(--sf-base-background-white);
    }
  }

  .modal-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 20px;
    padding: 16px;
    overflow-y: auto;
  }

  .modal-footer {
    padding: 16px;
    margin-top: auto;
  }
</style>
